import { aromaSidebar } from "@zenchef/styled-system/recipes";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import { HStack, Stack } from "@zenchef/styled-system/jsx";
import { useCallback, useRef, useState } from "react";
import { Text } from "../typography/Text";
import { SegmentGroup } from "@ark-ui/react";
import withTestId from "../../utils/withTestId";
import useMutationObserver from "../../utils/useMutationObserver";
import { useWindowSize } from "../../utils";
import { token } from "@zenchef/styled-system/tokens";
import SidebarContext from "./SidebarContext";
import SideBarSection from "./SidebarSection";
import SideBarItem from "./SidebarItem";
import useClickOutside from "../../utils/useClickOutside";

export interface SideBarProps {
  title?: string;
  onBack?: () => void;
  children?: React.ReactNode;
  defaultValue?: string;
  onValueChange?: SegmentGroup.RootProps["onValueChange"];
  value?: SegmentGroup.RootProps["value"];
  isCollapsible?: boolean;
  buttonZIndex?: string | number;
}

export const STANDALONE_SECTION_PREFIX = "--section--";

const SideBar = ({
  title,
  onBack,
  children,
  defaultValue,
  onValueChange,
  value,
  isCollapsible = true,
  buttonZIndex = "99",
  ...props
}: SideBarProps) => {
  const { width: windowWidth } = useWindowSize();
  const isDesktopScreen = windowWidth > parseInt(token("breakpoints.md"), 10);
  const isMobileScreen = windowWidth < parseInt(token("breakpoints.xs"), 10);
  const isSidebarFixed = localStorage.getItem("isSidebarFixed");
  const defaultIsFixed =
    isSidebarFixed === "true" || isDesktopScreen ? true : false;
  const defaultIsOpen = isMobileScreen
    ? false
    : defaultIsFixed || !isCollapsible;

  const hasHeader = Boolean(title ?? onBack);
  const [open, setOpen] = useState(defaultIsOpen);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(defaultIsFixed);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const className = aromaSidebar();

  const handleMouseEnter = () => {
    if (!isFixed && isCollapsible && !isMobileScreen) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isFixed && isCollapsible && !isMobileScreen) {
      setOpen(false);
      setSelectedSection(null);
    }
  };

  const toggleIsOpen = useCallback(() => {
    localStorage.setItem("isSidebarFixed", isFixed ? "false" : "true");
    setIsFixed(!isFixed);
    setOpen(!isFixed);
  }, [isFixed]);

  const onStackMutation = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const hasOverflow = container.scrollHeight > container.offsetHeight;
    setShowTopShadow(hasOverflow);
  }, [scrollContainerRef]);

  useMutationObserver(scrollContainerRef, onStackMutation);

  useClickOutside(sidebarRef, () => {
    if (isMobileScreen) {
      setOpen(false);
    }
  });

  return (
    <>
      <Stack
        ref={sidebarRef}
        gap="gap.0"
        data-part="sidebar"
        data-state={open ? "open" : "closed"}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        transform={
          isMobileScreen ? `translateX(${open ? "0" : "-100%"})` : undefined
        }
        visibility={isMobileScreen && !open ? "hidden" : "visible"}
        opacity={isMobileScreen && !open ? "0" : "1"}
        {...props}
      >
        {hasHeader ? (
          <HStack py="padding.3" px={open ? "padding.4" : "padding.3"}>
            <HStack data-part="sidebar-header" onClick={onBack}>
              <Button hierarchy="secondary-subtler" iconOnly>
                <Icon name="chevron-left" />
              </Button>
              {title && open && <Text textStyle="title.m">{title}</Text>}
            </HStack>
          </HStack>
        ) : null}
        <SegmentGroup.Root
          asChild
          defaultValue={defaultValue}
          onValueChange={(details) => {
            if (details.value.startsWith(STANDALONE_SECTION_PREFIX)) {
              onValueChange?.({
                ...details,
                value: details.value.replace(STANDALONE_SECTION_PREFIX, ""),
              });
            } else {
              onValueChange?.(details);
            }
          }}
          value={value}
        >
          <Stack
            ref={scrollContainerRef}
            flexGrow={2}
            gap="gap.4"
            py="padding.4"
            px={open ? "padding.4" : "padding.3"}
            overflowY="auto"
            height="0px"
            scrollbarWidth="none"
          >
            <SegmentGroup.Context>
              {({ value }) => (
                <SegmentGroup.Indicator
                  data-hidden={
                    ((!open || value?.startsWith(STANDALONE_SECTION_PREFIX)) ??
                      false) ||
                    undefined
                  }
                />
              )}
            </SegmentGroup.Context>
            <SidebarContext.Provider
              value={{ open, selectedSection, setSelectedSection }}
            >
              {children}
            </SidebarContext.Provider>
          </Stack>
        </SegmentGroup.Root>
        {isCollapsible ? (
          <HStack
            justifyContent="flex-end"
            gap="gap.2"
            padding="padding.2"
            boxShadow={showTopShadow ? "effect.shadow.subtle-top" : "none"}
          >
            <Button
              data-active={isFixed && !isMobileScreen ? "true" : undefined}
              hierarchy="secondary-subtler"
              iconOnly
              onClick={() => {
                if (isMobileScreen) {
                  setOpen(false);
                } else {
                  toggleIsOpen();
                }
              }}
            >
              <Icon name="sidebar" />
            </Button>
          </HStack>
        ) : null}
      </Stack>
      {isMobileScreen && !open ? (
        <Button
          hierarchy="reversed-bold"
          iconOnly
          onClick={() => setOpen(true)}
          position="absolute"
          left="padding.2"
          bottom="padding.2"
          style={{ zIndex: buttonZIndex }}
          transition="all 0.3s ease"
          boxShadow="effect.shadow.bold-bottom"
        >
          <Icon name="sidebar" />
        </Button>
      ) : null}
    </>
  );
};

export default Object.assign(withTestId("nav")(SideBar), {
  Section: SideBarSection,
  Item: SideBarItem,
});
